import moment from 'moment';
import { useEffect, useState } from 'react';

import * as api from '../../api';
import * as logger from '../../logger';

import { Notifications } from '../../scenes/Notifications';
import { TIME_MONTH_DAY_FORMAT } from '../../utils/dateTime';

export interface UseRescheduleReturnValues {
  onSlotSelected: (slot: api.EhrTimeSlot.EhrTimeSlot, timezone: string) => void;
  setRescheduleSlot: (slot: api.EhrTimeSlot.EhrTimeSlot) => void;
  rescheduleSlot?: api.EhrTimeSlot.EhrTimeSlot;
  availableEhrTimeSlots?: api.EhrTimeSlot.EhrTimeSlot[];
  pending?: boolean;
  error?: string;
  showManualRescheduling: boolean;
  rescheduled?: boolean;
}

interface Props {
  rescheduleIdentifier: api.EhrTimeSlot.RescheduleIdentifier;
  onRescheduled?: () => void;
  authenticationCode?: string;
}

function getReschedulingAppointmentParams(args: {
  slot: api.EhrTimeSlot.EhrTimeSlot;
  rescheduleIdentifier: api.EhrTimeSlot.RescheduleIdentifier;
  authenticationCode?: string;
}): api.EhrTimeSlot.AppointmentReschedulingBookingParams {
  const { slot, rescheduleIdentifier, authenticationCode } = args;
  const baseAppointment: Pick<
    api.EhrTimeSlot.AppointmentReschedulingBookingParams,
    'appointmentKind' | 'endTime' | 'newSlotId' | 'startTime'
  > = {
    appointmentKind: slot.appointmentKind,
    newSlotId: slot.id,
    startTime: slot.startTime,
    endTime: slot.endTime,
  };
  if ('ehrTimeSlotBookingId' in rescheduleIdentifier) {
    return {
      ...baseAppointment,
      ehrTimeSlotBookingId: rescheduleIdentifier.ehrTimeSlotBookingId,
    };
  }
  if ('existingEncounterId' in rescheduleIdentifier && authenticationCode) {
    return {
      ...baseAppointment,
      existingEncounterId: rescheduleIdentifier.existingEncounterId,
      authenticationCode,
    };
  }
  throw new Error('Unable to reschedule appointment.');
}

export const useReschedule = ({
  rescheduleIdentifier,
  onRescheduled,
  authenticationCode,
}: Props): UseRescheduleReturnValues => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showManualRescheduling, setShowManualRescheduling] = useState(false);
  const [availableEhrTimeSlots, setAvailableEhrTimeSlots] =
    useState<api.EhrTimeSlot.EhrTimeSlot[]>();
  const [rescheduleSlot, setRescheduleSlot] =
    useState<api.EhrTimeSlot.EhrTimeSlot>();
  useEffect(() => {
    const getSlots = async () => {
      if (!availableEhrTimeSlots) {
        setPending(true);
        try {
          const slotsResponse =
            await api.client.RescheduleEhrTimeSlot.getRescheduleSlots(
              rescheduleIdentifier,
            );
          setAvailableEhrTimeSlots(slotsResponse.availableEhrTimeSlots);
        } catch (e) {
          if (
            e.status ===
            api.ErrorHelper.STATUS_CODE_MAP[api.ErrorHelper.ErrorType.FORBIDDEN]
          ) {
            setShowManualRescheduling(true);
          } else {
            setError(e.toString());
          }
        } finally {
          setPending(false);
        }
      }
    };
    getSlots();
  }, [rescheduleIdentifier, availableEhrTimeSlots]);

  const onSlotSelected = async (
    slot: api.EhrTimeSlot.EhrTimeSlot,
    timezone: string,
  ) => {
    if (!rescheduleSlot) {
      return setError('Unable to reschedule appointment. No slot selected.');
    }
    setPending(true);
    setError(undefined);
    if ('existingEncounterId' in rescheduleIdentifier && !authenticationCode) {
      return;
    }
    try {
      const args = getReschedulingAppointmentParams({
        slot,
        rescheduleIdentifier,
        authenticationCode,
      });
      await api.client.RescheduleEhrTimeSlot.reschedule(args);
      Notifications.notify({
        body: `Your appointment has been successfully rescheduled to ${moment(
          slot.startTime,
        )
          .tz(timezone)
          .format(TIME_MONTH_DAY_FORMAT)}`,
        vertical: 'top',
      });
      onRescheduled && onRescheduled();
    } catch (e) {
      logger.error(e, `useReschedule.onSlotSelected failed`);
      setError(e.toString());
    } finally {
      setPending(false);
    }
  };
  return {
    availableEhrTimeSlots,
    onSlotSelected,
    pending,
    error,
    showManualRescheduling,
    setRescheduleSlot,
    rescheduleSlot,
  };
};
